import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDeals } from '../context/DataContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { fmtDate } from '../lib/format';
import InitialsAvatar from '../components/InitialsAvatar';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sparkles, Plus, Trash2, Paperclip, Check, Clock, X as XIcon, Calendar, Package, Mail, Search, Filter, Pencil } from 'lucide-react';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';

const emptyForm = (talent_id) => ({
  talent_id: talent_id || '',
  kind: 'event',
  name: '', event_datetime: '', rsvp_deadline: '', event_type: '', description: '',
  attachments: [],
  mailer_name: '', brand: '', brand_id: '', agency: '',
});

// -----------------------------------------------------------------------------
// Item card — one PR item or event, per talent
// -----------------------------------------------------------------------------
const RESPONSES = [
  { id: 'accepted', label: 'accept',  color: '#059669' },
  { id: 'declined', label: 'decline', color: '#e11d48' },
  { id: 'tbc',      label: 'tbc',     color: '#f59e0b' },
];

// quarter-hour time options for the event-time picker (time is optional), 7am–10pm only
const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 7; h <= 22; h++) {
    for (const m of [0, 15, 30, 45]) {
      if (h === 22 && m > 0) continue; // stop at 22:00 (10pm)
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return out;
})();
const NO_TIME = '__none__';
const NO_BRAND = '__nobrand__';
const NO_TYPE = '__notype__';
const EVENT_TYPES = ['drop in', 'meal', 'meeting', 'treatment', 'overnight', 'other', 'tbc'];

const isPast = (iso, withTime) => {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
};

// --- click-to-edit inline fields (manager only) ---
const EDIT_BTN = 'text-left rounded px-0.5 -mx-0.5 hover:bg-pink-50 cursor-text transition-colors';

const InlineText = ({ value, onSave, isManager, placeholder = '', className = '', inputClassName = 'h-7 text-sm', multiline = false, testid }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);
  if (!isManager) return value ? <span className={className}>{value}</span> : null;
  if (editing) {
    const commit = () => { setEditing(false); if ((draft || '') !== (value || '')) onSave(draft); };
    if (multiline) {
      return (
        <Textarea
          autoFocus value={draft}
          onChange={(e) => setDraft(e.target.value)} onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Escape') { setEditing(false); setDraft(value || ''); } }}
          rows={2} className="text-[11px] resize-none border-pink-200" placeholder={placeholder}
          data-testid={testid}
        />
      );
    }
    return (
      <Input
        autoFocus value={draft}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } if (e.key === 'Escape') { setEditing(false); setDraft(value || ''); } }}
        className={`${inputClassName} border-pink-200`} placeholder={placeholder}
        data-testid={testid}
      />
    );
  }
  return (
    <button type="button" onClick={() => setEditing(true)} data-testid={testid}
      className={`${EDIT_BTN} ${className} ${!value ? 'text-slate-300 italic' : ''}`}>
      {value || placeholder}
    </button>
  );
};

const InlineDate = ({ value, onSave, isManager, placeholder = '+ add', testid }) => {
  const [editing, setEditing] = useState(false);
  if (!isManager) return value ? <span>{fmtDate(value)}</span> : null;
  if (editing) {
    return (
      <Input type="date" autoFocus defaultValue={value || ''}
        onBlur={(e) => { setEditing(false); if ((e.target.value || '') !== (value || '')) onSave(e.target.value); }}
        className="h-7 text-[11px] w-auto inline-block border-pink-200" data-testid={testid} />
    );
  }
  return (
    <button type="button" onClick={() => setEditing(true)} data-testid={testid}
      className={`${EDIT_BTN} ${!value ? 'text-slate-300 italic' : ''}`}>
      {value ? fmtDate(value) : placeholder}
    </button>
  );
};

const ItemCard = ({ item, isManager, onPatch, onDelete, onEdit }) => {
  const { brands } = useDeals();
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState(item.notes || '');
  useEffect(() => { setNotesDraft(item.notes || ''); }, [item.notes]);

  // inline edit helpers
  const pickInlineBrand = (id) => {
    if (id === NO_BRAND) { onPatch({ brand_id: '', brand: '' }); return; }
    const b = brands.find((x) => x.id === id);
    onPatch({ brand_id: id, brand: b?.name || '' });
  };
  const whenDate = (item.event_datetime || '').split('T')[0] || '';
  const whenTime = (item.event_datetime || '').includes('T') ? (item.event_datetime.split('T')[1] || '').slice(0, 5) : '';
  const setWhen = (d, t) => onPatch({ event_datetime: d ? (t ? `${d}T${t}` : d) : '' });

  // Colour rules — red if RSVP overdue & no response, grey if event passed & no response
  const rsvpOverdue = item.kind === 'event' && isPast(item.rsvp_deadline) && !item.talent_response && !item.actioned;
  const eventPassed = item.kind === 'event' && isPast(item.event_datetime) && !item.talent_response && !item.actioned;

  const cardTone = rsvpOverdue
    ? 'bg-rose-50 border-rose-300'
    : eventPassed
      ? 'bg-slate-100 border-slate-300 opacity-80'
      : 'bg-white border-slate-200';

  const respondPill = (id) => {
    const r = RESPONSES.find((x) => x.id === id);
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 h-6 rounded-full text-[11px] font-semibold ${item.actioned ? '' : 'ring-1 ring-slate-900/30'}`}
        style={{ background: `${r.color}22`, color: r.color }}
      >{r.label}ed</span>
    );
  };

  return (
    <div className={`group relative rounded-xl border p-3 shadow-sm ${cardTone}`} data-testid={`pr-item-${item.id}`}>
      {isManager && (
        <button
          onClick={() => onPatch({ actioned: !item.actioned })}
          className={`absolute top-2 right-2 w-5 h-5 rounded border flex items-center justify-center transition-colors ${item.actioned ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-300 hover:border-slate-500'}`}
          title={item.actioned ? 'un-mark actioned' : 'mark actioned'}
          data-testid={`pr-actioned-${item.id}`}
        >{item.actioned && <Check className="w-3 h-3" />}</button>
      )}

      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 mb-1">
        {item.kind === 'event' ? <Calendar className="w-3 h-3" /> : <Package className="w-3 h-3" />}
        <span>{item.kind === 'event' ? 'event' : 'pr'}</span>
        {rsvpOverdue && <span className="text-rose-600 font-semibold">· rsvp overdue</span>}
        {!rsvpOverdue && eventPassed && <span className="text-slate-500">· passed</span>}
      </div>

      {item.kind === 'event' ? (
        <>
          <InlineText value={item.name} onSave={(v) => onPatch({ name: v })} isManager={isManager}
            placeholder="untitled event" className="block text-sm font-semibold text-slate-800 w-full"
            inputClassName="h-7 text-sm font-semibold" testid={`pr-f-name-${item.id}`} />
          <div className="flex flex-wrap items-center gap-1 mt-1">
            {isManager ? (
              <Select value={item.event_type || NO_TYPE} onValueChange={(v) => onPatch({ event_type: v === NO_TYPE ? '' : v })}>
                <SelectTrigger className="h-5 px-1.5 w-auto gap-1 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-700 border-0 capitalize focus:ring-0" data-testid={`pr-f-type-${item.id}`}>
                  <SelectValue placeholder="+ type">{item.event_type || '+ type'}</SelectValue>
                </SelectTrigger>
                <SelectContent><SelectItem value={NO_TYPE}>none</SelectItem>{EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            ) : (item.event_type && <span className="inline-flex items-center px-1.5 h-5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-700 capitalize">{item.event_type}</span>)}
            {isManager ? (
              <Select value={item.brand_id || NO_BRAND} onValueChange={pickInlineBrand}>
                <SelectTrigger className="h-5 px-1.5 w-auto gap-1 rounded-full text-[10px] font-semibold bg-pink-100 text-pink-700 border-0 focus:ring-0" data-testid={`pr-f-brand-${item.id}`}>
                  <SelectValue placeholder="+ brand">{item.brand || '+ brand'}</SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-64"><SelectItem value={NO_BRAND}>no brand</SelectItem>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            ) : (item.brand && <span className="inline-flex items-center px-1.5 h-5 rounded-full text-[10px] font-semibold bg-pink-100 text-pink-700">{item.brand}</span>)}
          </div>
          {(isManager || item.event_datetime) && (
            <div className="text-[11px] text-slate-600 mt-0.5 flex items-center gap-1 flex-wrap">
              <span className="font-medium">when · </span>
              <InlineDate value={whenDate} onSave={(d) => setWhen(d, whenTime)} isManager={isManager} placeholder="+ add date" testid={`pr-f-date-${item.id}`} />
              {whenDate && (isManager ? (
                <Select value={whenTime || NO_TIME} onValueChange={(v) => setWhen(whenDate, v === NO_TIME ? '' : v)}>
                  <SelectTrigger className="h-6 px-1.5 w-auto text-[11px] border-pink-200" data-testid={`pr-f-time-${item.id}`}><SelectValue placeholder="+ time" /></SelectTrigger>
                  <SelectContent className="max-h-64"><SelectItem value={NO_TIME}>no time</SelectItem>{TIME_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              ) : (whenTime && <span>· {whenTime}</span>))}
            </div>
          )}
          {(isManager || item.rsvp_deadline) && (
            <div className="text-[11px] text-slate-600 flex items-center gap-1">
              <span className="font-medium">rsvp by · </span>
              <InlineDate value={item.rsvp_deadline} onSave={(d) => onPatch({ rsvp_deadline: d })} isManager={isManager} placeholder="+ add" testid={`pr-f-rsvp-${item.id}`} />
            </div>
          )}
          {(isManager || item.description) && (
            <InlineText value={item.description} onSave={(v) => onPatch({ description: v })} isManager={isManager}
              placeholder="+ add description" className="block text-[11px] text-slate-600 mt-1 whitespace-pre-wrap w-full"
              multiline testid={`pr-f-desc-${item.id}`} />
          )}
          {(item.attachments || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {item.attachments.map((att) => (
                <a
                  key={att.id}
                  href={att.url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${att.url}` : att.url}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-600 hover:border-pink-300 hover:text-pink-600"
                >
                  <Paperclip className="w-2.5 h-2.5" />
                  <span className="truncate max-w-[120px]">{att.name || 'invite'}</span>
                </a>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <InlineText value={item.mailer_name} onSave={(v) => onPatch({ mailer_name: v })} isManager={isManager}
            placeholder="mailer" className="block text-sm font-semibold text-slate-800 w-full"
            inputClassName="h-7 text-sm font-semibold" testid={`pr-f-mailer-${item.id}`} />
          {(isManager || item.brand) && (
            <div className="text-[11px] text-slate-600 mt-0.5 flex items-center gap-1">
              <span className="font-medium">brand · </span>
              {isManager ? (
                <Select value={item.brand_id || NO_BRAND} onValueChange={pickInlineBrand}>
                  <SelectTrigger className="h-6 px-1.5 w-auto text-[11px] border-pink-200" data-testid={`pr-f-brand-${item.id}`}><SelectValue placeholder="+ brand">{item.brand || '+ brand'}</SelectValue></SelectTrigger>
                  <SelectContent className="max-h-64"><SelectItem value={NO_BRAND}>no brand</SelectItem>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : <span>{item.brand}</span>}
            </div>
          )}
          {(isManager || item.agency) && (
            <div className="text-[11px] text-slate-600 flex items-center gap-1">
              <span className="font-medium">agency · </span>
              <InlineText value={item.agency} onSave={(v) => onPatch({ agency: v })} isManager={isManager}
                placeholder="+ add" inputClassName="h-6 text-[11px] inline-block w-auto" testid={`pr-f-agency-${item.id}`} />
            </div>
          )}
          {(isManager || item.description) && (
            <InlineText value={item.description} onSave={(v) => onPatch({ description: v })} isManager={isManager}
              placeholder="+ add notes" className="block text-[11px] text-slate-600 mt-1 whitespace-pre-wrap w-full"
              multiline testid={`pr-f-desc-${item.id}`} />
          )}
        </>
      )}

      {/* Talent response strip */}
      <div className="mt-2 pt-2 border-t border-slate-100 flex items-center flex-wrap gap-1">
        {item.talent_response ? (
          <div className={`inline-flex items-center gap-1.5 ${item.actioned ? '' : 'font-bold'}`}>
            {respondPill(item.talent_response)}
            <span className="text-[10px] text-slate-400">{item.talent_response_at ? fmtDate(item.talent_response_at) : ''}</span>
          </div>
        ) : (
          RESPONSES.map((r) => (
            <button
              key={r.id}
              onClick={() => onPatch({ talent_response: r.id })}
              className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] font-semibold border border-slate-200 bg-white hover:brightness-95"
              style={{ color: r.color }}
              data-testid={`pr-respond-${item.id}-${r.id}`}
            >{r.label}</button>
          ))
        )}
        {isManager && item.talent_response && (
          <button
            onClick={() => onPatch({ talent_response: null })}
            className="ml-auto text-[10px] text-slate-400 hover:text-red-600"
            title="clear talent's response (they can pick again)"
          ><XIcon className="w-3 h-3" /></button>
        )}
      </div>

      {/* Notes */}
      <div className="mt-2 pt-2 border-t border-slate-100">
        {notesEditing && isManager ? (
          <Textarea
            autoFocus value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            onBlur={() => { setNotesEditing(false); if (notesDraft !== (item.notes || '')) onPatch({ notes: notesDraft }); }}
            onKeyDown={(e) => { if (e.key === 'Escape') { setNotesEditing(false); setNotesDraft(item.notes || ''); } }}
            rows={2}
            className="text-[11px] resize-none border-pink-200"
            placeholder="add a note…"
          />
        ) : (
          <button
            type="button"
            onClick={() => isManager && setNotesEditing(true)}
            disabled={!isManager}
            className={`text-left w-full text-[11px] whitespace-pre-wrap ${item.notes ? 'text-slate-600' : 'text-slate-300 italic'} ${isManager ? 'cursor-text hover:text-slate-800' : ''}`}
          >
            {item.notes || (isManager ? '+ add a note' : '')}
          </button>
        )}
      </div>

      {isManager && (
        <div className="absolute bottom-2 right-2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit?.()}
            className="text-slate-400 hover:text-pink-600"
            title="edit details"
            data-testid={`pr-edit-${item.id}`}
          ><Pencil className="w-3 h-3" /></button>
          <button
            onClick={() => { if (window.confirm('Delete this item?')) onDelete(); }}
            className="text-slate-400 hover:text-red-600"
            title="delete"
            data-testid={`pr-delete-${item.id}`}
          ><Trash2 className="w-3 h-3" /></button>
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Add item dialog
// -----------------------------------------------------------------------------
const AddItemDialog = ({ open, onOpenChange, talentId, editItem, onCreated }) => {
  const { brands } = useDeals();
  const [form, setForm] = useState(emptyForm(talentId));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setForm({
        talent_id: editItem.talent_id || '',
        kind: editItem.kind || 'event',
        name: editItem.name || '',
        event_datetime: editItem.event_datetime || '',
        rsvp_deadline: editItem.rsvp_deadline || '',
        event_type: editItem.event_type || '',
        description: editItem.description || '',
        attachments: editItem.attachments || [],
        mailer_name: editItem.mailer_name || '',
        brand: editItem.brand || '',
        brand_id: editItem.brand_id || '',
        agency: editItem.agency || '',
      });
    } else {
      setForm(emptyForm(talentId));
    }
  }, [open, talentId, editItem]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // pick a brand from brand contacts → stores both the id (link) and the name (display).
  // The brand name also leads the title: the event name for events, the mailer name for PR
  // (e.g. "Byoma dinner" / "Byoma summer box").
  const pickBrand = (id) => {
    setForm((f) => {
      const titleKey = f.kind === 'event' ? 'name' : 'mailer_name';
      const prev = f.brand || '';
      const stripPrefix = (name) => (prev && name.toLowerCase().startsWith(prev.toLowerCase()))
        ? name.slice(prev.length).replace(/^\s+/, '') : name;
      if (id === NO_BRAND) {
        return { ...f, brand_id: '', brand: '', [titleKey]: stripPrefix(f[titleKey] || '') };
      }
      const b = brands.find((x) => x.id === id);
      const newBrand = b?.name || '';
      let title = stripPrefix(f[titleKey] || '');
      if (!title.toLowerCase().startsWith(newBrand.toLowerCase())) {
        title = `${newBrand}${title ? ' ' + title : ''}`;
      }
      return { ...f, brand_id: id, brand: newBrand, [titleKey]: title };
    });
  };

  const uploadPdf = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/uploads/pdf', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('attachments', [...(form.attachments || []), { id: data.id, name: data.name, url: data.url }]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const submit = async () => {
    if (form.kind === 'event' && !form.name.trim()) { toast.error('event needs a name'); return; }
    if (form.kind === 'pr' && !form.mailer_name.trim() && !form.brand.trim() && !form.agency.trim()) {
      toast.error('give the pr item at least one of: mailer / brand / agency'); return;
    }
    setSaving(true);
    try {
      let data;
      if (editItem) {
        const payload = {
          kind: form.kind, name: form.name, event_datetime: form.event_datetime,
          rsvp_deadline: form.rsvp_deadline, event_type: form.event_type || null, description: form.description, attachments: form.attachments,
          mailer_name: form.mailer_name, brand: form.brand, brand_id: form.brand_id || null, agency: form.agency,
        };
        ({ data } = await api.patch(`/pr-events/${editItem.id}`, payload));
      } else {
        ({ data } = await api.post('/pr-events', form));
      }
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editItem ? 'edit pr / event' : 'add pr / event'}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="inline-flex items-center bg-slate-100 rounded-full p-0.5">
            {[
              { id: 'event', label: 'event',  icon: Calendar },
              { id: 'pr',    label: 'pr',     icon: Package },
            ].map((opt) => {
              const Icon = opt.icon;
              const active = form.kind === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => set('kind', opt.id)}
                  className={`px-3 h-8 text-xs font-semibold rounded-full inline-flex items-center gap-1 transition ${active ? 'bg-white text-pink-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                  data-testid={`pr-kind-${opt.id}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {opt.label}
                </button>
              );
            })}
          </div>

          {form.kind === 'event' ? (
            <>
              <div><Label>Event name</Label><Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Aritzia SS26 launch dinner" /></div>
              {(() => {
                const evtDate = (form.event_datetime || '').split('T')[0] || '';
                const evtTime = (form.event_datetime || '').includes('T') ? (form.event_datetime.split('T')[1] || '').slice(0, 5) : '';
                const setDateTime = (date, time) => set('event_datetime', date ? (time ? `${date}T${time}` : date) : '');
                return (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Event date</Label>
                      <Input type="date" value={evtDate} onChange={(e) => setDateTime(e.target.value, evtTime)} data-testid="pr-event-date" />
                    </div>
                    <div>
                      <Label>Time <span className="text-slate-400 text-[11px]">(optional)</span></Label>
                      <Select value={evtTime || NO_TIME} onValueChange={(v) => setDateTime(evtDate, v === NO_TIME ? '' : v)} disabled={!evtDate}>
                        <SelectTrigger className="mt-0" data-testid="pr-event-time"><SelectValue placeholder="no time" /></SelectTrigger>
                        <SelectContent className="max-h-64">
                          <SelectItem value={NO_TIME}>no time (date only)</SelectItem>
                          {TIME_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })()}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>RSVP deadline</Label><Input type="date" value={form.rsvp_deadline} onChange={(e) => set('rsvp_deadline', e.target.value)} /></div>
                <div>
                  <Label>Event type <span className="text-slate-400 text-[11px]">(optional)</span></Label>
                  <Select value={form.event_type || NO_TYPE} onValueChange={(v) => set('event_type', v === NO_TYPE ? '' : v)}>
                    <SelectTrigger data-testid="pr-event-type"><SelectValue placeholder="select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_TYPE}>none</SelectItem>
                      {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Brand <span className="text-slate-400 text-[11px]">(optional — from brand contacts)</span></Label>
                <Select value={form.brand_id || NO_BRAND} onValueChange={pickBrand}>
                  <SelectTrigger data-testid="pr-event-brand"><SelectValue placeholder="link a brand contact" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value={NO_BRAND}>no brand</SelectItem>
                    {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.brand && !form.brand_id && <p className="text-[11px] text-slate-400 mt-1">currently: {form.brand}</p>}
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={3} placeholder="dress code, venue, notes…" />
              </div>
              <div>
                <Label>Invite PDFs</Label>
                <div className="mt-1 flex items-center flex-wrap gap-1.5">
                  {(form.attachments || []).map((att) => (
                    <span key={att.id} className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded px-2 py-1 text-xs">
                      <Paperclip className="w-3 h-3 text-slate-500" />
                      {att.name || 'file.pdf'}
                      <button onClick={() => set('attachments', form.attachments.filter((a) => a.id !== att.id))} className="text-slate-400 hover:text-red-600"><XIcon className="w-3 h-3" /></button>
                    </span>
                  ))}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPdf(f); }}
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center gap-1 text-xs text-pink-600 hover:text-pink-700 border border-dashed border-pink-300 rounded px-2 py-1 disabled:opacity-60"
                  >
                    <Paperclip className="w-3 h-3" />
                    {uploading ? 'uploading…' : 'attach pdf'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div><Label>Mailer name <span className="text-slate-400 text-[11px]">(optional)</span></Label><Input value={form.mailer_name} onChange={(e) => set('mailer_name', e.target.value)} placeholder="e.g. Byoma summer refresh box" /></div>
              <div>
                <Label>Brand <span className="text-slate-400 text-[11px]">(optional)</span></Label>
                <div className="flex items-center gap-2">
                  <Input value={form.brand} onChange={(e) => set('brand', e.target.value)} placeholder="e.g. Byoma" className="flex-1" />
                  <Select value={form.brand_id || NO_BRAND} onValueChange={pickBrand}>
                    <SelectTrigger className="w-40 shrink-0" data-testid="pr-pr-brand"><SelectValue placeholder="from contacts" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value={NO_BRAND}>from contacts…</SelectItem>
                      {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Agency <span className="text-slate-400 text-[11px]">(optional)</span></Label><Input value={form.agency} onChange={(e) => set('agency', e.target.value)} placeholder="e.g. Purple PR" /></div>
              <div>
                <Label>Notes / description <span className="text-slate-400 text-[11px]">(optional)</span></Label>
                <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={2} />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-pink-500 hover:bg-pink-600 text-white">{saving ? 'saving…' : (editItem ? 'save' : 'add')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// -----------------------------------------------------------------------------
// Talent section (one board per talent)
// -----------------------------------------------------------------------------
const TalentBoard = ({ talent, items, isManager, onCreate, onPatch, onDelete }) => {
  const [showActioned, setShowActioned] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const visible = items.filter((i) => showActioned ? i.actioned : !i.actioned);
  const actionedCount = items.filter((i) => i.actioned).length;

  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-4">
      <div className="flex items-center gap-3 mb-3">
        <InitialsAvatar name={talent.name} size={32} className="text-xs" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-800 truncate">{talent.name}</div>
          <div className="text-[11px] text-slate-500">{visible.length} {showActioned ? 'actioned' : 'open'} · {items.length} total</div>
        </div>
        <button
          onClick={() => setShowActioned((s) => !s)}
          className={`text-[11px] font-semibold rounded-full px-3 h-7 transition ${showActioned ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          title="switch between open and completed items"
          data-testid={`pr-toggle-actioned-${talent.user_id}`}
        >{showActioned ? `back to open` : `actioned · ${actionedCount}`}</button>
        {isManager && (
          <Button size="sm" onClick={() => { setEditItem(null); setAddOpen(true); }} className="bg-pink-500 hover:bg-pink-600 text-white h-7 text-xs" data-testid={`pr-add-${talent.user_id}`}>
            <Plus className="w-3.5 h-3.5 mr-1" /> add
          </Button>
        )}
      </div>
      {visible.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-lg p-6 text-center text-xs text-slate-400">
          {showActioned ? 'no completed items yet' : (isManager ? 'no open pr/events — click add to start' : 'nothing waiting for you')}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {visible.map((i) => (
            <ItemCard
              key={i.id}
              item={i}
              isManager={isManager}
              onPatch={(u) => onPatch(i.id, u)}
              onDelete={() => onDelete(i.id)}
              onEdit={() => { setEditItem(i); setAddOpen(true); }}
            />
          ))}
        </div>
      )}
      <AddItemDialog
        open={addOpen}
        onOpenChange={(v) => { setAddOpen(v); if (!v) setEditItem(null); }}
        talentId={talent.user_id}
        editItem={editItem}
        onCreated={onCreate}
      />
    </section>
  );
};

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
const PrEvents = () => {
  const { user } = useAuth();
  const { talent } = useDeals();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get('/pr-events');
      setItems(data || []);
    } catch { toast.error('failed to load pr + events'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onCreate = (item) => setItems((prev) => (
    prev.some((x) => x.id === item.id) ? prev.map((x) => x.id === item.id ? item : x) : [item, ...prev]
  ));
  const onPatch = async (id, patch) => {
    try {
      const { data } = await api.patch(`/pr-events/${id}`, patch);
      setItems((prev) => prev.map((x) => x.id === id ? data : x));
    } catch (e) { toast.error(e?.response?.data?.detail || 'update failed'); }
  };
  const onDelete = async (id) => {
    try {
      await api.delete(`/pr-events/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch { toast.error('delete failed'); }
  };

  const [q, setQ] = useState('');
  const [talentFilter, setTalentFilter] = useState('all');

  const grouped = useMemo(() => {
    const roster = isManager ? talent : talent.filter((t) => t.user_id === user?.user_id);
    const byTalent = talentFilter === 'all' ? roster : roster.filter((t) => t.user_id === talentFilter);
    const term = q.trim().toLowerCase();
    const matches = (t, its) => {
      if (!term) return true;
      if ((t.name || '').toLowerCase().includes(term)) return true;
      return its.some((i) => [i.name, i.mailer_name, i.brand, i.agency, i.description].filter(Boolean).some((v) => v.toLowerCase().includes(term)));
    };
    return byTalent
      .map((t) => ({ talent: t, items: items.filter((i) => i.talent_id === t.user_id) }))
      .filter((g) => matches(g.talent, g.items));
  }, [talent, items, isManager, user, talentFilter, q]);

  return (
    <div className="p-6 lg:p-8">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-pink-500" /> pr + events
          </h1>
          <p className="text-sm text-slate-500 mt-1">gifting drops + event invites — track rsvps, attendance and status per talent.</p>
        </div>
      </header>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search talent, events, mailers…" className="pl-9 h-10 bg-white" data-testid="pr-search" />
        </div>
        {isManager && (
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={talentFilter} onValueChange={setTalentFilter}>
              <SelectTrigger className="h-10 w-44 bg-white" data-testid="pr-talent-filter"><SelectValue placeholder="talent" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">all talent</SelectItem>
                {talent.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">loading…</div>
      ) : grouped.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-12 text-center text-sm text-slate-400">
          {talent.length === 0 ? 'no talent on your roster yet' : 'no talent matches your search/filter'}
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map((g) => (
            <TalentBoard
              key={g.talent.user_id}
              talent={g.talent}
              items={g.items}
              isManager={isManager}
              onCreate={onCreate}
              onPatch={onPatch}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PrEvents;
